import React, { useState } from "react";
import { FileText, Download, RefreshCw } from "lucide-react";
import { useAuditLogs, useAuditLogSummary } from "../hooks/useAuditLogs";
import { AuditLogTable } from "../components/auditLogs/AuditLogTable";
import { AuditLogFilters } from "../components/auditLogs/AuditLogFilters";
import { AuditLogDetails } from "../components/auditLogs/AuditLogDetails";
import { Pagination } from "../components/common/Pagination";
import { AuditLog } from "../api/services/auditLogs/types";
import toast from "react-hot-toast";

export const AuditLogs: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTable, setSelectedTable] = useState("");
  const [selectedAction, setSelectedAction] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  const itemsPerPage = 50;
  const offset = (currentPage - 1) * itemsPerPage;

  const { data, isLoading, isError, error, refetch } = useAuditLogs({
    limit: itemsPerPage,
    offset,
    table_name: selectedTable || undefined,
    action: (selectedAction as "INSERT" | "UPDATE" | "DELETE") || undefined,
    start_date: startDate || undefined,
    end_date: endDate || undefined,
  });

  const { data: summaryData } = useAuditLogSummary(7);

  const handleReset = () => {
    setSearchTerm("");
    setSelectedTable("");
    setSelectedAction("");
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
  };

  const handleViewDetails = (log: AuditLog) => {
    setSelectedLog(log);
    setShowDetails(true);
  };

  const handleExport = () => {
    if (!filteredLogs || filteredLogs.length === 0) {
      toast.error("No data to export");
      return;
    }

    // Define CSV headers
    const headers = [
      "Timestamp",
      "Action",
      "Table",
      "Record ID",
      "User Name",
      "User Email",
      "User Role",
      "Changed Fields",
      "Client IP",
      "User Agent",
    ];

    // Convert logs to CSV rows
    const rows = filteredLogs.map((log) => [
      new Date(log.action_timestamp).toLocaleString(),
      log.action,
      log.table_name,
      log.record_id || "",
      log.user_name || "System",
      log.user_email || "",
      log.user_role || "",
      log.changed_fields ? JSON.stringify(log.changed_fields) : "",
      log.client_ip || "",
      log.user_agent || "",
    ]);

    // Create CSV content
    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        row
          .map((cell) => {
            // Escape quotes and wrap in quotes if contains comma or newline
            const cellStr = String(cell);
            if (
              cellStr.includes(",") ||
              cellStr.includes("\n") ||
              cellStr.includes('"')
            ) {
              return `"${cellStr.replace(/"/g, '""')}"`;
            }
            return cellStr;
          })
          .join(","),
      ),
    ].join("\n");

    // Create and download the file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `audit-logs-${new Date().toISOString().split("T")[0]}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(`Exported ${filteredLogs.length} audit logs`);
  };

  const handleRefresh = () => {
    refetch();
    toast.success("Audit logs refreshed");
  };

  // Filter logs based on search term
  const filteredLogs =
    data?.logs?.filter((log) => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return (
        log.user_name?.toLowerCase().includes(term) ||
        log.user_email?.toLowerCase().includes(term) ||
        log.table_name.toLowerCase().includes(term) ||
        log.record_id?.toLowerCase().includes(term)
      );
    }) || [];

  const totalPages = data ? Math.ceil(data.total / itemsPerPage) : 0;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div></div>
        <div className="flex gap-2">
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Download className="h-4 w-4" />
            Export
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summaryData && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Changes</p>
                <p className="text-2xl font-bold text-gray-900">
                  {(summaryData.action_counts.INSERT || 0) +
                    (summaryData.action_counts.UPDATE || 0) +
                    (summaryData.action_counts.DELETE || 0)}
                </p>
              </div>
              <FileText className="h-8 w-8 text-gray-400" />
            </div>
          </div>

          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600">Inserts</p>
                <p className="text-2xl font-bold text-green-900">
                  {summaryData.action_counts.INSERT || 0}
                </p>
              </div>
              <div className="h-8 w-8 bg-green-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold">+</span>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600">Updates</p>
                <p className="text-2xl font-bold text-blue-900">
                  {summaryData.action_counts.UPDATE || 0}
                </p>
              </div>
              <div className="h-8 w-8 bg-blue-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold">↻</span>
              </div>
            </div>
          </div>

          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600">Deletes</p>
                <p className="text-2xl font-bold text-red-900">
                  {summaryData.action_counts.DELETE || 0}
                </p>
              </div>
              <div className="h-8 w-8 bg-red-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold">−</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <AuditLogFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        selectedTable={selectedTable}
        onTableChange={setSelectedTable}
        selectedAction={selectedAction}
        onActionChange={setSelectedAction}
        startDate={startDate}
        onStartDateChange={setStartDate}
        endDate={endDate}
        onEndDateChange={setEndDate}
        onReset={handleReset}
      />

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : isError ? (
          <div className="p-8 text-center">
            <p className="text-red-600">Error loading audit logs</p>
            <p className="text-sm text-gray-600 mt-2">
              {error?.message || "Please try again later"}
            </p>
          </div>
        ) : (
          <AuditLogTable
            logs={filteredLogs}
            onViewDetails={handleViewDetails}
          />
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}

      {/* Details Modal */}
      {showDetails && selectedLog && (
        <AuditLogDetails
          log={selectedLog}
          onClose={() => {
            setShowDetails(false);
            setSelectedLog(null);
          }}
        />
      )}
    </div>
  );
};
